import { useEffect, useState } from 'react';

import Typography from '~/shared/components/Typography';

const PROGRESS_LEVELS = {
  low: { defId: 'progressRed', color: 'var(--color-red)' },
  medium: { defId: 'progressOrange', color: 'var(--color-orange)' },
  high: { defId: 'progressGreen', color: 'var(--color-green)' },
} as const;

function getProgressLevel(percentage: number) {
  if (percentage > 79) return PROGRESS_LEVELS.high;
  if (percentage > 50) return PROGRESS_LEVELS.medium;
  return PROGRESS_LEVELS.low;
}

interface CircularProgressProps {
  percentage: number;
  size?: number;
  strokeWidth?: number;
  animationDuration?: number;
  loading?: boolean;
}

export default function CircularProgress({
  percentage,
  size = 78,
  strokeWidth = 9,
  animationDuration = 1000,
  loading = false,
}: CircularProgressProps) {
  const [animatedPercentage, setAnimatedPercentage] = useState(0);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (animatedPercentage / 100) * circumference;
  const level = getProgressLevel(percentage);
  const strokeDefId = `${level.defId}-${size}`;

  useEffect(() => {
    const startTime = Date.now();
    const startPercentage = 0;
    const endPercentage = percentage;

    const animate = () => {
      const currentTime = Date.now();
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / animationDuration, 1);

      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentPercentage = startPercentage + (endPercentage - startPercentage) * easeOut;

      setAnimatedPercentage(currentPercentage);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [percentage, animationDuration]);

  return (
    <div
      role="progressbar"
      aria-valuenow={Math.round(animatedPercentage)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label="Progress"
      className="relative grid place-items-center"
      style={{ width: size, height: size }}
    >
      <svg
        className="absolute top-0 left-0"
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
      >
        <defs>
          {Object.values(PROGRESS_LEVELS).map(({ defId, color }) => (
            <linearGradient key={defId} id={`${defId}-${size}`} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor={color} />
              <stop offset="100%" stopColor={color} />
            </linearGradient>
          ))}
        </defs>
        <circle
          className="text-gray-200"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        <circle
          className="transform rotate-270 origin-center"
          stroke={`url(#${strokeDefId})`}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
      </svg>
      {!loading && (
        <Typography variant="H5" color="text-primary-900" className="font-family-inter">
          {`${Math.round(animatedPercentage)}%`}
        </Typography>
      )}
    </div>
  );
}
